/** @format */

import { Grid } from '@material-ui/core';
import { format } from 'date-fns';
import { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams, Link } from 'react-router-dom';
import { Redirect } from 'react-router';
import { Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import {
  KnowledgeBaseItem,
  KnowledgeBaseListSkeleton,
  KnowledgeBasePageSkeleton
} from 'src/components/knowledegeBase/item';
import { KnowledgeBaseSearch } from 'src/components/knowledegeBase/search';
import Page from 'src/components/Page';
import SelectComponent from 'src/components/select';
import useQuery from 'src/hooks/useQuery';
import { getKnowledgeBase, getKnowledgeBaseArticles } from 'src/slices/knowledgeBase';
import { useDispatch, useSelector } from 'src/store';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

export default function KnowledgeBase() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { kb } = useParams();
  const query = useQuery().get('query');
  const { t } = useTranslation();
  const { knowledgeBase, knowledgeBaseLoading, knowledgeBaseArticles, knowledgeBaseArticlesLoading, error } =
    useSelector(state => state.knowledgeBase);
  const [category, setCategory] = useState(0);
  const [sort, setSort] = useState('cerated_desc');

  const [displayedArticles, setDisplayedArticles] = useState({ loading: true, articles: [] });
  const prevArticlesLoadingRef = useRef(knowledgeBaseArticlesLoading);

  useEffect(() => {
    dispatch(getKnowledgeBase(kb));
  }, [dispatch, kb]);

  useEffect(() => {
    dispatch(
      getKnowledgeBaseArticles({
        kb: kb || 0,
        categories: category ? [category] : null,
        sort,
        query: query || ''
      })
    );
  }, [category, sort, query, dispatch, kb]);

  useEffect(() => {
    const wasLoading = prevArticlesLoadingRef.current;
    prevArticlesLoadingRef.current = knowledgeBaseArticlesLoading;

    if (wasLoading && !knowledgeBaseArticlesLoading) {
      startViewTransition(() => {
        setDisplayedArticles({ loading: false, articles: knowledgeBaseArticles });
      });
    } else if (knowledgeBaseArticlesLoading && !wasLoading) {
      setDisplayedArticles({ loading: true, articles: [] });
    } else if (!knowledgeBaseArticlesLoading) {
      setDisplayedArticles({ loading: false, articles: knowledgeBaseArticles });
    }
  }, [knowledgeBaseArticlesLoading, knowledgeBaseArticles]);

  const pageTitle = knowledgeBase?.title || '';

  if (error || !kb) return <Redirect to="/404" />;
  if (knowledgeBaseLoading) {
    return (
      <Page className={`knowledge-base`} title={pageTitle}>
        <Breadcrumb data={[{ title: pageTitle }]} additionClassName="container-narrow" />
        <Grid className="knowledge-base-container container container-narrow">
          <KnowledgeBasePageSkeleton />
        </Grid>
      </Page>
    );
  }
  return (
    <Page className={`knowledge-base`} title={pageTitle}>
      <Breadcrumb data={[{ title: pageTitle }]} additionClassName="container-narrow" />
      <Grid className="knowledge-base-container container container-narrow">
        <Grid className="knowledge-base-title-box">
          <h1 className="knowledge-base-title">{pageTitle}</h1>
          <KnowledgeBaseSearch kb={kb} />
        </Grid>
        <Grid className="knowledge-base-text" dangerouslySetInnerHTML={{ __html: knowledgeBase?.body }} />
        <Grid className="knowledge-base-categories-sort">
          <h3>{t('knowledgeBase.categories')}</h3>
          <Grid className="knowledge-base-categories-sort-flex">
            <ul className="knowledge-base-categories">
              <li>
                <button className={category === 0 ? `active` : ''} onClick={() => setCategory(0)}>
                  {t('knowledgeBase.all')}
                </button>
              </li>
              {knowledgeBase?.categories?.map(cat => (
                <li key={cat.id}>
                  <button className={category === cat.id ? `active` : ''} onClick={() => setCategory(cat.id)}>
                    {cat.name}
                  </button>
                </li>
              ))}
            </ul>
            <Grid className="knowledge-base-sort">
              <SelectComponent
                label={t('knowledgeBase.sortBy')}
                options={[
                  { value: 'cerated_desc', name: t('knowledgeBase.newestDesc') },
                  { value: 'cerated_asc', name: t('knowledgeBase.newestAsc') },
                  { value: 'title_asc', name: t('knowledgeBase.alphabeticalAsc') },
                  { value: 'title_desc', name: t('knowledgeBase.alphabeticalDesc') }
                ]}
                onChange={e => e.target.value && setSort(e.target.value)}
                value={sort}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid className="knowledge_base-widget--list knowledge-base-list" style={{ viewTransitionName: 'kb-list' }}>
          {displayedArticles.loading ? (
            <KnowledgeBaseListSkeleton count={5} />
          ) : displayedArticles.articles?.length > 0 ? (
            displayedArticles.articles.map(item => <KnowledgeBaseItem item={item} kb={kb} key={item.nid} />)
          ) : (
            <p className="knowledge_base-widget--list-empty">{t('knowledgeBase.noResults')}</p>
          )}
        </Grid>
        <Button onClick={() => history.push(`/dashboard`)} borderOnly back label={t('knowledgeBase.backToHome')} />
      </Grid>
    </Page>
  );
}
